import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import SignIn from './SignIn';
import KanbanBoard from './KanbanBoard';
import { useKanban } from './KanbanContext';
import TaskForm from './TaskForm';
import WorkspaceManager from './WorkspaceManager';
import flow2Image from '../assets/flow2.png';
import useEscapeKey from '../utils/useEscapeKey';

function WorkspaceContent() {
  const { 
    workspaceData, 
    activeWorkspace, 
    setActiveWorkspace, 
    userAccessibleWorkspaces, 
    workspacesLoading, 
    addTask,
    reloadTasksFromServer,
    canUserDoOnBoard
  } = useKanban();
  const { apiLoading, apiError } = useAuth();
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [isWorkspaceManagerOpen, setIsWorkspaceManagerOpen] = useState(false);
  const [workspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false);
  const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');
  const workspaceSelectorRef = React.useRef(null);
  
  // Efecto para recargar tareas después del inicio de sesión
  useEffect(() => {
    // Verificar si hay una bandera para recargar tareas
    const shouldRefresh = sessionStorage.getItem('refreshTasksOnLogin');
    
    if (shouldRefresh === 'true' && activeWorkspace && !workspacesLoading) {
      // Recargar tareas después del inicio de sesión
      reloadTasksFromServer(activeWorkspace);
      // Limpiar la bandera después de recargar
      sessionStorage.removeItem('refreshTasksOnLogin');
    }
  }, [activeWorkspace, workspacesLoading, reloadTasksFromServer]);
  
  // Efecto para cerrar el selector de workspaces cuando se hace clic fuera de él
  useEffect(() => {
    function handleClickOutside(event) {
      if (workspaceSelectorRef.current && !workspaceSelectorRef.current.contains(event.target)) {
        setWorkspaceSelectorOpen(false);
      }
    }

    // Agregar event listener cuando el selector está abierto
    if (workspaceSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [workspaceSelectorOpen]);
  
  // Manejar el botón de retroceso del navegador para modales
  useEffect(() => {
    // Solo agregar un estado al historial cuando se abre un modal
    if (isWorkspaceManagerOpen) {
      // Agregar una entrada al historial cuando se abre un modal
      window.history.pushState({ modal: true }, "");
      
      // Manejador para el evento popstate (botón atrás)
      const handlePopState = (event) => {
        // Cerrar el modal
        setIsWorkspaceManagerOpen(false);
        
        // Prevenir que el navegador vaya hacia atrás realmente
        if (event.state !== null) {
          window.history.pushState({ modal: true }, "");
        }
      };
      
      // Agregar el manejador de eventos
      window.addEventListener('popstate', handlePopState);
      
      // Limpiar el manejador al desmontar
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isWorkspaceManagerOpen]);
  
  // Cerrar modales con la tecla Escape
  useEscapeKey(() => {
    if (isWorkspaceManagerOpen) {
      setIsWorkspaceManagerOpen(false);
    } else if (isTaskFormOpen) {
      setIsTaskFormOpen(false);
    }
  }, isWorkspaceManagerOpen || isTaskFormOpen);
  
  const handleAddTask = async (newTask) => {
    try {
      const targetWorkspace = newTask.workspace || activeWorkspace;
      if (!canUserDoOnBoard('createTasks', targetWorkspace)) return;
      await addTask(targetWorkspace, newTask);
      setIsTaskFormOpen(false);
    } catch (error) {
      // Aquí podrías agregar una notificación al usuario sobre el error
      alert('Error: ' + error.message);
    }
  };

  // Mostrar estado de carga mientras se obtienen los datos del API
  if (apiLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando datos de usuario...</p>
      </div>
    );
  }

  // Mostrar mensaje de error si falla la llamada a la API
  if (apiError) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <p>Error al cargar datos de usuario. Por favor, recarga la página o contacta con soporte.</p>
      </div>
    );
  }

  // Mostrar estado de carga mientras se verifica el acceso a los workspaces
  // También mostrar carga si no hay workspaces accesibles (puede ser que la API aún esté cargando)
  if (workspacesLoading || userAccessibleWorkspaces.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando acceso a tableros...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Main Board Area - Now full width with even spacing */}
      <main className="board-container full-width">
        {userAccessibleWorkspaces.length > 0 ? (
          <>
            <header className="board-header">
              <div className="board-header-content">
                <div className="workspace-selector" ref={workspaceSelectorRef}>
                  <div className="workspace-selector-content">
                    <button 
                      className="workspace-selector-button"
                      onClick={() => setWorkspaceSelectorOpen(!workspaceSelectorOpen)}
                      aria-haspopup="true"
                      aria-expanded={workspaceSelectorOpen}
                    >
                      <div className="workspace-info">
                        <span className="workspace-title">{workspaceData.find(w => w.id === activeWorkspace)?.name}</span>
                      </div>
                      <span className="dropdown-arrow">▼</span>
                    </button>
                    
                    {workspaceSelectorOpen && (
                      <div className="workspace-selector-menu">
                        {/* Buscador de tableros */}
                        <div className="workspace-search-container">
                          <input
                            type="text"
                            placeholder="Buscar tablero..."
                            value={workspaceSearchTerm}
                            onChange={(e) => setWorkspaceSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="workspace-search-input"
                            autoFocus
                          />
                          {workspaceSearchTerm && (
                            <button 
                              className="workspace-search-clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                setWorkspaceSearchTerm('');
                              }}
                              title="Limpiar búsqueda"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        
                        {userAccessibleWorkspaces.length > 0 ? (
                          (() => {
                            // Filtrar workspaces según el término de búsqueda
                            const filteredWorkspaces = workspaceSearchTerm.trim() === ''
                              ? userAccessibleWorkspaces
                              : userAccessibleWorkspaces.filter(workspace =>
                                  workspace.name.toLowerCase().includes(workspaceSearchTerm.toLowerCase())
                                );
                            
                            return filteredWorkspaces.length > 0 ? (
                              filteredWorkspaces.map(workspace => (
                                <div 
                                  key={workspace.id}
                                  className={`workspace-selector-item ${activeWorkspace === workspace.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setActiveWorkspace(workspace.id);
                                    setWorkspaceSelectorOpen(false);
                                    setWorkspaceSearchTerm('');
                                  }}
                                  title={workspace.isUserTasksOnly ? `Solo tus tareas (${workspace.userTaskCount})` : workspace.name}
                                >
                                  <span className="workspace-name">
                                    {workspace.name}
                                    {workspace.isUserTasksOnly && (
                                      <span className="user-indicator" title="Solo tus tareas">
                                        👤 ({workspace.userTaskCount})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="no-workspaces-message">
                                <p>No se encontraron tableros con "{workspaceSearchTerm}"</p>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="no-workspaces-message">
                            <p>No tienes acceso a ningún tablero.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {canUserDoOnBoard('createTasks') && (
                    <div className="workspace-buttons">
                      <button 
                        className="new-task-btn"
                        onClick={() => setIsTaskFormOpen(true)}
                        title="Nueva Tarea"
                      >
                        +<span className="btn-text"> Nueva Tarea</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>
            
            <div className="board-content">
              <KanbanBoard showTaskForm={isTaskFormOpen} setShowTaskForm={setIsTaskFormOpen} onAddTask={handleAddTask} />
            </div>
          </>
        ) : null}
      </main>

      {/* Task Form Modal */}
      {isTaskFormOpen && (
        <div className="task-form-overlay" onClick={() => setIsTaskFormOpen(false)}>
          <div className="task-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-form-header">
              <h3>Crear Nueva Tarea</h3>
              <button 
                className="close-btn"
                onClick={() => setIsTaskFormOpen(false)}
              >
                ✕
              </button>
            </div>
            <TaskForm 
              onComplete={() => setIsTaskFormOpen(false)} 
              onAddTask={handleAddTask}
            />
          </div>
        </div>
      )}
      
      {/* Workspace Manager Modal */}
      {isWorkspaceManagerOpen && (
        <div className="workspace-manager-overlay" onClick={() => setIsWorkspaceManagerOpen(false)}>
          <div className="workspace-manager-modal" onClick={(e) => e.stopPropagation()}>
            <WorkspaceManager onClose={() => setIsWorkspaceManagerOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function Home() {
  const { currentUser, apiLoading } = useAuth();
  
  // Siempre llamar al hook, independientemente de si estamos en el contexto o no
  // y luego verificar si los valores devueltos son válidos
  const kanbanContext = useKanban();
  
  // Si no estamos dentro del KanbanProvider, kanbanContext podría ser un objeto vacío
  // o tener propiedades undefined
  const workspacesLoading = kanbanContext?.workspacesLoading;

  if (!currentUser) {
    return (
      <div className="login-container">
        {/* Fondo animado */}
        <div className="background-lines">
          {/* Primera línea ondulada */}
          <div className="wave-line">
            <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M0,50 C200,100 400,0 600,50 C800,100 1000,0 1200,50 C1400,100 1600,0 1800,50" 
                fill="none" 
                stroke="rgba(0,0,0,0.15)" 
                strokeWidth="3"
              />
              <circle cx="200" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="600" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="1000" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="1400" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
            </svg>
          </div>
          
          {/* Segunda línea ondulada */}
          <div className="wave-line">
            <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M0,20 C150,80 300,0 450,50 C600,100 750,30 900,70 C1050,20 1200,80 1350,30" 
                fill="none" 
                stroke="rgba(0,0,0,0.15)" 
                strokeWidth="3"
              />
              <circle cx="150" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="450" cy="50" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="750" cy="40" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="1050" cy="60" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="1350" cy="30" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
            </svg>
          </div>
          
          {/* Tercera línea ondulada */}
          <div className="wave-line">
            <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M0,70 C180,20 360,90 540,40 C720,0 900,60 1080,40 C1260,20 1440,70 1620,30" 
                fill="none" 
                stroke="rgba(0,0,0,0.15)" 
                strokeWidth="3"
              />
              <circle cx="180" cy="40" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="540" cy="40" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="900" cy="60" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
              <circle cx="1260" cy="20" r="10" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="3" />
            </svg>
          </div>
          
          {/* Cuarta línea ondulada (nueva) */}
          <div className="wave-line">
            <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M0,30 C160,70 320,10 480,60 C640,110 800,20 960,50 C1120,80 1280,10 1440,40" 
                fill="none" 
                stroke="rgba(0,0,0,0.12)" 
                strokeWidth="2"
              />
              <circle cx="160" cy="70" r="7" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="2" />
              <circle cx="480" cy="60" r="7" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="2" />
              <circle cx="800" cy="20" r="7" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="2" />
              <circle cx="1120" cy="80" r="7" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="2" />
              <circle cx="1440" cy="40" r="7" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="2" />
            </svg>
          </div>
          
          {/* Quinta línea ondulada (nueva) */}
          <div className="wave-line">
            <svg viewBox="0 0 1440 100" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M0,85 C120,35 240,75 360,25 C480,0 600,50 720,80 C840,35 960,65 1080,25 C1200,50 1320,15 1440,60" 
                fill="none" 
                stroke="rgba(0,0,0,0.1)" 
                strokeWidth="2"
              />
              <circle cx="120" cy="35" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
              <circle cx="360" cy="25" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
              <circle cx="600" cy="50" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
              <circle cx="840" cy="35" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
              <circle cx="1080" cy="25" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
              <circle cx="1320" cy="15" r="6" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="2" />
            </svg>
          </div>
          
          {/* Puntos flotantes de diferentes tamaños */}
          <div className="dot dot-small"></div>
          <div className="dot dot-small"></div>
          <div className="dot dot-small"></div>
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot dot-large"></div>
          <div className="dot dot-large"></div>
          <div className="dot dot-small"></div>
          <div className="dot dot-small"></div>
          <div className="dot"></div>
          <div className="dot dot-large"></div>
          <div className="dot"></div>
        </div>
        
        {/* Tarjeta de login */}
        <div className="login-card">
          <div className="login-logo">
            <img src={flow2Image} alt="XinerLink Flows" className="login-image" />
          </div>
          <h2 className="login-title">Portal de Tableros</h2>
          <div className="login-form">
            <SignIn />
          </div>
        </div>
      </div>
    );
  }

  // Si estamos cargando los datos del usuario o verificando acceso a tableros,
  // mostrar pantalla de carga en lugar de WorkspaceContent
  if (apiLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando datos de usuario...</p>
      </div>
    );
  }
  
  // Solo mostrar loading para workspaces si el contexto Kanban está disponible y está cargando
  if (kanbanContext && workspacesLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando acceso a tableros...</p>
      </div>
    );
  }

  return <WorkspaceContent />;
}

export default Home; 